/**
 * ExMarked — Local Filesystem Source — IndexedDB Persistence
 *
 * FileSystemDirectoryHandle is structured-cloneable and can be stored
 * in IndexedDB, but cannot be JSON-serialised to Supabase. All local
 * source metadata (handle + display info) lives here.
 */

const DB_NAME    = 'exmarked'
const DB_VERSION = 1
const STORE_NAME = 'local-sources'

// ─── Record ───────────────────────────────────────────────────────────────────

export interface LocalSourceRecord {
  id:               string
  display_name:     string
  path:             string   // top-level directory name (for display)
  handle:           FileSystemDirectoryHandle
  added_at:         string
  last_accessed_at: string | null
}

// ─── DB bootstrap ─────────────────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror   = () => reject(req.error)
  })
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

export async function saveLocalSource(record: LocalSourceRecord): Promise<void> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put(record)
    req.onsuccess = () => resolve()
    req.onerror   = () => reject(req.error)
  })
}

export async function getAllLocalSources(): Promise<LocalSourceRecord[]> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll()
    req.onsuccess = () => resolve(req.result as LocalSourceRecord[])
    req.onerror   = () => reject(req.error)
  })
}

export async function getLocalSource(id: string): Promise<LocalSourceRecord | undefined> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(id)
    req.onsuccess = () => resolve(req.result as LocalSourceRecord | undefined)
    req.onerror   = () => reject(req.error)
  })
}

export async function deleteLocalSource(id: string): Promise<void> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).delete(id)
    req.onsuccess = () => resolve()
    req.onerror   = () => reject(req.error)
  })
}

export async function touchLocalSource(id: string): Promise<void> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx     = db.transaction(STORE_NAME, 'readwrite')
    const store  = tx.objectStore(STORE_NAME)
    const getReq = store.get(id)
    getReq.onsuccess = () => {
      const record = getReq.result as LocalSourceRecord | undefined
      if (!record) { resolve(); return }
      record.last_accessed_at = new Date().toISOString()
      const putReq = store.put(record)
      putReq.onsuccess = () => resolve()
      putReq.onerror   = () => reject(putReq.error)
    }
    getReq.onerror = () => reject(getReq.error)
  })
}

// ─── Permission helper ────────────────────────────────────────────────────────

/**
 * Checks (and if needed requests) read permission for a stored handle.
 * Returns true if granted. May show a browser permission prompt.
 */
export async function verifyPermission(handle: FileSystemDirectoryHandle): Promise<boolean> {
  const opts: FileSystemHandlePermissionDescriptor = { mode: 'read' }
  if ((await handle.queryPermission(opts)) === 'granted') return true
  return (await handle.requestPermission(opts)) === 'granted'
}

/** True if the browser supports the File System Access API. */
export function supportsLocalFS(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window
}
